<script setup lang="ts">
import BPLogoIcon from "@/assets/svg/bp-logo-white.svg?component";
import BPLogoSmall from "@/assets/img/bp-logo-small.png";
import IBGLogoIcon from "@/assets/svg/ibg-logo-white.svg?component";
import IBGLogoSmall from "@/assets/svg/ibg-logo-white-small.svg?component";
import IBGLogoBlackIcon from "@/assets/svg/ibg-logo-black.svg?component";
import BarsIcon from "@/assets/svg/bars.svg?component";
import XMarkIcon from "@/assets/svg/xmark.svg?component";
import NavMenu from "@/stories/navigations/NavMenu/NavMenu.vue";
import { useRoute } from "vue-router";
import { computed, type Ref } from "vue";
import { useBreakpoints } from "@vueuse/core";
import {
  BREAKPOINTS,
  IS_BP_MANAGER,
  IS_IBG_MANAGER,
  MOBILE_NAV_HEIGHT,
} from "@/consts";
import { ref } from "vue";
import MobileNavMenu from "@/stories/navigations/NavMenu/MobileNavMenu.vue";
import Line from "@/stories/layout/Line/Line.vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth.module";
import InstitutionSwitcher from "@/stories/navigations/InstitutionSwitcher/InstitutionSwitcher.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import LoggedInAs from "@/stories/navigations/NavMenu/LoggedInAs.vue";
import type { SVGName } from "@/SVGs";
import SVG from "@/stories/displays/SVG/SVG.vue";
import NavigationDotMenu from "@/components/layouts/navigations/NavigationDotMenu.vue";
import { useCan } from "@/stores/useCan";

interface ILink {
  value: string;
  title: string;
  icon: SVGName;
  ibgOnly?: boolean;
  bpOnly?: boolean;
  flag?: string;
  internalWsOnly?: boolean;
  permission?: Ref<boolean>;
}

const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isTablet = breakpoints.greaterOrEqual("tablet");
const isDesktop = breakpoints.greaterOrEqual("desktop");
const { can } = useCan();

const LINKS: ILink[] = [
  {
    value: "/",
    title: "global.dashboard",
    icon: "fas-rectangle-list",
    ibgOnly: true,
    permission: can("view", "dashboard"),
  },
  {
    value: "/our-home/employees",
    title: "global.ourhome",
    icon: "fas-house-user",
    ibgOnly: true,
  },
  {
    value: "/meeting/meetings",
    title: "global.meetings",
    icon: "fas-messages",
    ibgOnly: true,
  },
  {
    value: "/news",
    title: "global.news",
    icon: "fas-messages-lines",
  },
  {
    value: "/shift-plan/schedule-plan",
    title: "global.scheduleplan",
    icon: "fas-id-card",
    ibgOnly: true,
    internalWsOnly: true,
  },
  {
    value: "/booking/bookable-bookings",
    title: "global.booking",
    icon: "fas-key",
    ibgOnly: true,
  },
  {
    value: "/meal/meals",
    title: "global.mealplan",
    icon: "fas-utensils",
    ibgOnly: true,
  },
  {
    value: "/meals",
    title: "Meals (New)",
    icon: "fas-utensils",
    ibgOnly: true,
    permission: can("view", "meals"),
  },
  {
    value: "/activities",
    title: "global.activities",
    icon: "fas-person-running",
  },
  {
    value: "/employees",
    title: "global.employees",
    icon: "fas-users",
    bpOnly: true,
  },
  {
    value: "/media/galleries",
    title: "global.media",
    icon: "fas-photo-video",
    ibgOnly: true,
  },
  {
    value: "/groups",
    title: "global.groups",
    icon: "fas-users",
    ibgOnly: true,
  },
  {
    value: "/planner",
    title: "global.planner",
    icon: "fas-clipboard-check",
    ibgOnly: true,
    permission: can("view", "planner"),
  },
];

const isMobileMenuActive = ref(false);
const loading = ref(false);

const translatedLinks = computed(() => {
  // Don't show IBG only menu items when using BP Manager
  return LINKS.filter((link) => {
    // It should be shown if there is not any flag
    if (!link.flag) {
      return true;
    }

    // Let's check if the flag is activated
    return localStorage.getItem(link.flag);
  })
    .filter((link) => {
      if (IS_BP_MANAGER && link.ibgOnly === true) return;
      if (IS_IBG_MANAGER && link.bpOnly === true) return;

      // Only show work schedule if there are no external work schedules
      // set up for the currently active institution
      if (
        link.internalWsOnly &&
        authStore.currentInstitution?.settings.useExternalWorkSchedule
      )
        return;
      return link;
    })
    .filter((link) => {
      if (link.permission !== undefined) {
        return link.permission.value;
      }

      return true;
    })
    .map((link) => {
      return {
        ...link,
        title: t(link.title),
      };
    });
});

const currentRoute = computed(() => {
  if (isRouteAdmin.value) {
    return;
  }

  if (route.name === "support") {
    return;
  }

  const matched = LINKS.filter((item) =>
    route.path.toString().startsWith(item.value),
  );

  return matched.at(-1) || LINKS.at(0);
});

const isRouteAdmin = computed(() => {
  return route.path.toString() === "/admin";
});

const handleLogout = async () => {
  loading.value = true;
  await authStore.logout();
};
</script>

<template>
  <LoadingDialog v-if="loading" />

  <nav
    :class="[
      'bg-darkOcean700 left-0 fixed top-0 h-dvh flex flex-col justify-between text-white z-10',
      'tablet:w-[5rem]',
      'desktop:w-[13.625rem]',
      'print:hidden',
    ]"
    id="mobile-navigation"
    v-if="(authStore.isAuthenticated || authStore.loggingOut) && isTablet"
  >
    <div class="h-20 flex justify-center items-center">
      <RouterLink to="/" class="focus-visible-inverse rounded">
        <BPLogoIcon
          v-if="IS_BP_MANAGER && isDesktop"
          width="8.8125rem"
          height="1.5625rem"
        />
        <img
          v-if="IS_BP_MANAGER && !isDesktop"
          :src="BPLogoSmall"
          class="h-[1.875rem] w-[1.875rem]"
        />
        <IBGLogoIcon
          v-if="IS_IBG_MANAGER && isDesktop"
          width="8.125rem"
          height="2.125rem"
        />
        <IBGLogoSmall
          v-if="IS_IBG_MANAGER && !isDesktop"
          width="4.5rem"
          height="5rem"
        />
      </RouterLink>
    </div>
    <div
      :class="[
        'flex-grow flex flex-col items-center justify-center gap-1 overflow-y-auto navigation-scroller',
        'tablet:px-1',
        'desktop:px-4 desktop:justify-start',
        'short:justify-start',
      ]"
    >
      <NavMenu
        v-for="link of translatedLinks"
        :key="link.value"
        :icon="link.icon"
        :title="link.title"
        :to="link.value"
        :active="currentRoute?.value === link.value"
      />
    </div>

    <div
      v-if="IS_IBG_MANAGER"
      class="pb-6 px-4 justify-center items-center hidden desktop:flex"
    >
      <RouterLink
        to="/support"
        class="bg-darkOcean600 w-full flex items-center p-3 rounded-full gap-2 h300 relative focus-visible-inverse"
      >
        <SVG icon="fas-circle-question" width="1.25rem" height="1.25rem"></SVG>
        <div class="flex-grow">
          {{ $t("support.navigation_link") }}
        </div>
      </RouterLink>
    </div>

    <LoggedInAs class="pl-4" />

    <div class="desktop:px-4 pb-8 flex flex-col justify-center items-center">
      <div class="bg-neutral500 opacity-20 mb-2 h-[1px] w-full"></div>
      <NavigationDotMenu v-if="!isDesktop" @logout="handleLogout" />

      <NavMenu
        v-if="authStore.isSupport && IS_IBG_MANAGER"
        key="admin"
        icon="fas-user-gear"
        to="/admin"
        class="hidden desktop:flex"
        :title="$t('global.admin')"
        :active="isRouteAdmin"
      />

      <NavMenu
        as="button"
        icon="fas-right-from-bracket"
        class="hidden desktop:flex"
        :title="$t('main.logout')"
        @click="handleLogout"
      />
    </div>
  </nav>

  <nav
    v-if="(authStore.isAuthenticated || authStore.loggingOut) && !isTablet"
    class="bg-white flex items-center justify-between px-4 border-b border-neutral300 relative"
    :style="{ height: MOBILE_NAV_HEIGHT }"
  >
    <RouterLink to="/">
      <BPLogoIcon v-if="IS_BP_MANAGER" width="8.8125rem" height="1.5625rem" />
      <IBGLogoBlackIcon
        v-if="IS_IBG_MANAGER"
        width="8.125rem"
        height="2.125rem"
      />
    </RouterLink>
    <button
      type="button"
      @click="isMobileMenuActive = !isMobileMenuActive"
      class="transition-all"
    >
      <BarsIcon
        v-if="!isMobileMenuActive"
        class="animate-in fade-in"
        width="2rem"
        height="2rem"
      />
      <XMarkIcon
        v-if="isMobileMenuActive"
        class="animate-in fade-in"
        width="2rem"
        height="2rem"
      />
    </button>
    <div
      v-if="isMobileMenuActive"
      :class="`fixed left-0 w-full bg-white py-10 px-4 animate-in fade-in z-50 overflow-auto`"
      :style="{
        top: MOBILE_NAV_HEIGHT,
        height: `calc(100dvh - ${MOBILE_NAV_HEIGHT})`,
      }"
    >
      <div class="flex flex-col gap-8 pb-3">
        <InstitutionSwitcher
          v-if="
            ((isMobileMenuActive && authStore.profile?.institutions?.length) ||
              0) > 1
          "
        ></InstitutionSwitcher>

        <MobileNavMenu
          v-for="link of translatedLinks"
          :key="link.value"
          :icon="link.icon"
          :title="link.title"
          :to="link.value"
          :active="currentRoute?.value === link.value"
          @click="isMobileMenuActive = false"
        />
      </div>

      <LoggedInAs class="mt-4" is-mobile />

      <Line padding="lg" />
      <div class="pt-3">
        <MobileNavMenu
          v-if="authStore.isSupport && IS_IBG_MANAGER"
          class="mb-6"
          key="admin"
          icon="fas-lock"
          :title="$t('global.admin')"
          to="/admin"
          :active="isRouteAdmin"
          @click="isMobileMenuActive = false"
        />

        <button
          type="button"
          class="h600 flex items-center gap-3"
          @click="handleLogout"
        >
          <SVG icon="far-sign-out" width="2rem" height="2rem"></SVG>
          {{ $t("main.logout") }}
        </button>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.navigation-scroller {
  scrollbar-width: thin; /* Firefox: slim scrollbar */
  scrollbar-color: #769297 #194a51; /* thumb | track */
}

/* Chrome, Edge, Safari */
.navigation-scroller::-webkit-scrollbar {
  width: 0.6875rem; /* total scrollbar container width */
}

.navigation-scroller::-webkit-scrollbar-thumb {
  background-color: #769297; /* scrollbar thumb color */
  border-radius: 0.5rem; /* rounded corners */
  border: 2px solid #194a51; /* creates "padding" so thumb looks 0.4375rem */
}

.navigation-scroller::-webkit-scrollbar-track {
  background: #194a51; /* track background color */
}
</style>
